/**
 * Environment variables with type safety and validation
 */

/**
 * Parse a comma-separated origin allowlist into a Set of exact origins.
 *
 * Used to validate `event.origin` on inbound `postMessage` from the Hub
 * studio-viewer parent before parsing the charge-session payloads. Entries are
 * trimmed; empty entries are dropped. No wildcards — matching is exact.
 */
function parseAllowedOrigins(raw: string | undefined): Set<string> {
  if (!raw) {
    return new Set<string>();
  }
  return new Set(
    raw
      .split(',')
      .map((origin) => origin.trim())
      .filter((origin) => origin.length > 0)
  );
}

/**
 * DEV-ONLY: when the local mock parent is active, the SPA must accept the
 * charge-session messages it posts to itself, so its own origin has to be in the
 * allowlist. Gated on `import.meta.env.DEV` AND `mock=1` AND
 * `mapType=chargeSessions` so it is impossible in production builds (the whole
 * branch is dead-code-eliminated) and never widens the allowlist for a normal
 * dev session.
 *
 * NOTE: this is the SAME gate as `isMockChargeSessionsActive` in
 * `features/charge-sessions/dev/mock-charge-sessions.ts`. The two MUST stay in
 * sync; they are duplicated rather than shared so this central env module never
 * imports a feature module. See the charge-sessions "Local mock mode" docs.
 */
function resolveHubAllowedOrigins(): ReadonlySet<string> {
  const origins = parseAllowedOrigins(import.meta.env.VITE_HUB_ALLOWED_ORIGINS);
  const params = new URLSearchParams(window.location.search);
  const isMockActive =
    import.meta.env.DEV &&
    params.get('mock') === '1' &&
    params.get('mapType') === 'chargeSessions';
  if (isMockActive) {
    origins.add(window.location.origin);
  }
  return origins;
}

export const env = {
  ENV: import.meta.env.VITE_ENV || 'development',
  API_URL: import.meta.env.VITE_API_URL || 'https://api.dev.monta.app',
  GOOGLE_MAPS_API_KEY: import.meta.env.VITE_PUBLIC_GOOGLE_MAPS_API_KEY || '',
  SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN || '',
  // PostHog configuration
  PUBLIC_POSTHOG_KEY: import.meta.env.VITE_PUBLIC_POSTHOG_KEY || '',
  PUBLIC_POSTHOG_HOST: import.meta.env.VITE_PUBLIC_POSTHOG_HOST || '',
  // Charge-session map (BIZOPS-1588): exact origins allowed to drive the
  // iframe via postMessage. Comma-separated; empty in dev unless configured.
  // In a DEV build with `?mock=1`, `window.location.origin` is also allowed so
  // the local mock parent can self-drive (see resolveHubAllowedOrigins).
  HUB_ALLOWED_ORIGINS: resolveHubAllowedOrigins(),
  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
} as const;

// Validate required environment variables
if (!env.GOOGLE_MAPS_API_KEY && env.isProduction) {
  console.warn('VITE_PUBLIC_GOOGLE_MAPS_API_KEY is not set');
}

if (!env.PUBLIC_POSTHOG_KEY && env.isProduction) {
  console.warn('VITE_PUBLIC_POSTHOG_KEY is not set. Analytics disabled.');
}
